import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Locales, LocalesType, toLocaleName } from "../../../model/Core";

interface LegalDisclosureFormProps {
  draft: API.LegalDisclosureDraft;
  disabled?: boolean;
  onChange: (draft: API.LegalDisclosureDraft) => void;
}

const emptyLicense = (): API.RegulatoryLicense => ({
  authority: "",
  label: "",
  number: "",
  jurisdiction: "",
  verificationUrl: "",
});

const parseDocumentId = (value: string): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

export const LegalDisclosureForm = ({
  draft,
  disabled = false,
  onChange,
}: LegalDisclosureFormProps) => {
  const change = (patch: Partial<API.LegalDisclosureDraft>) => {
    onChange({ ...draft, ...patch });
  };

  const changeRegistration = (patch: Partial<API.CompanyRegistration>) => {
    change({
      companyRegistration: { ...draft.companyRegistration, ...patch },
    });
  };

  const changeBindings = (patch: Partial<API.LegalDocumentBindings>) => {
    change({
      documentBindings: { ...draft.documentBindings, ...patch },
    });
  };

  const changeDomain = (index: number, value: string) => {
    const affectedDomains = [...draft.affectedDomains];
    affectedDomains[index] = value;
    change({ affectedDomains });
  };

  const changeStatement = (
    index: number,
    patch: Partial<API.LegalDisclosureStatement>
  ) => {
    const statements = draft.statements.map((statement, statementIndex) =>
      statementIndex === index ? { ...statement, ...patch } : statement
    );
    change({ statements });
  };

  const changeLicense = (
    index: number,
    patch: Partial<API.RegulatoryLicense>
  ) => {
    const regulatoryLicenses = draft.regulatoryLicenses.map(
      (license, licenseIndex) =>
        licenseIndex === index ? { ...license, ...patch } : license
    );
    change({ regulatoryLicenses });
  };

  return (
    <Stack spacing={2.5}>
      <Card variant="outlined">
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={draft.enabled}
                disabled={disabled}
                onChange={(event) => change({ enabled: event.target.checked })}
              />
            }
            label="启用运营主体声明"
          />
          <Typography variant="body2" color="text.secondary">
            默认关闭。保存草稿不会影响前台，只有发布且启用后才会展示。
          </Typography>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            适用域名
          </Typography>
          <Stack spacing={1}>
            {draft.affectedDomains.map((domain, index) => (
              <Stack direction="row" spacing={1} key={index}>
                <TextField
                  fullWidth
                  size="small"
                  label={`域名 ${index + 1}`}
                  value={domain}
                  disabled={disabled}
                  placeholder="www.example.com"
                  helperText="只填写主机名，不包含协议、端口或路径"
                  onChange={(event) => changeDomain(index, event.target.value)}
                />
                <IconButton
                  aria-label={`删除域名 ${index + 1}`}
                  disabled={disabled}
                  onClick={() =>
                    change({
                      affectedDomains: draft.affectedDomains.filter(
                        (_, domainIndex) => domainIndex !== index
                      ),
                    })
                  }
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            <Box>
              <Button
                startIcon={<AddIcon />}
                disabled={disabled}
                onClick={() =>
                  change({
                    affectedDomains: [...draft.affectedDomains, ""],
                  })
                }
              >
                添加域名
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            公司登记信息
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="运营主体公司名称"
                value={draft.operatorName}
                disabled={disabled}
                onChange={(event) =>
                  change({ operatorName: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="注册司法辖区"
                value={draft.jurisdiction}
                disabled={disabled}
                placeholder="Colorado, United States"
                onChange={(event) =>
                  change({ jurisdiction: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="注册地址"
                value={draft.registeredAddress}
                disabled={disabled}
                onChange={(event) =>
                  change({ registeredAddress: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="公司登记机构"
                value={draft.companyRegistration.authority}
                disabled={disabled}
                placeholder="Colorado Secretary of State"
                onChange={(event) =>
                  changeRegistration({ authority: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="登记编号名称"
                value={draft.companyRegistration.label}
                disabled={disabled}
                placeholder="Colorado Entity ID"
                onChange={(event) =>
                  changeRegistration({ label: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="公司登记编号"
                value={draft.companyRegistration.number}
                disabled={disabled}
                onChange={(event) =>
                  changeRegistration({ number: event.target.value })
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="公司登记核验链接（可选）"
                value={draft.companyRegistration.verificationUrl || ""}
                disabled={disabled}
                placeholder="https://..."
                onChange={(event) =>
                  changeRegistration({ verificationUrl: event.target.value })
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6">监管牌照（可选）</Typography>
          <Alert severity="warning" sx={{ my: 1.5 }}>
            这里只能填写运营主体自身的金融监管牌照。EIN、公司登记编号和合作方资质不能作为监管牌照。
          </Alert>
          <Stack spacing={2}>
            {draft.regulatoryLicenses.map((license, index) => (
              <Box key={index}>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="subtitle1">牌照 {index + 1}</Typography>
                  <IconButton
                    aria-label={`删除牌照 ${index + 1}`}
                    disabled={disabled}
                    onClick={() =>
                      change({
                        regulatoryLicenses: draft.regulatoryLicenses.filter(
                          (_, licenseIndex) => licenseIndex !== index
                        ),
                      })
                    }
                  >
                    <DeleteOutlineIcon />
                  </IconButton>
                </Stack>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="监管机构"
                      value={license.authority}
                      disabled={disabled}
                      onChange={(event) =>
                        changeLicense(index, { authority: event.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="牌照编号名称"
                      value={license.label}
                      disabled={disabled}
                      onChange={(event) =>
                        changeLicense(index, { label: event.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      label="牌照编号"
                      value={license.number}
                      disabled={disabled}
                      onChange={(event) =>
                        changeLicense(index, { number: event.target.value })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="牌照司法辖区"
                      value={license.jurisdiction}
                      disabled={disabled}
                      onChange={(event) =>
                        changeLicense(index, {
                          jurisdiction: event.target.value,
                        })
                      }
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="牌照核验链接（可选）"
                      value={license.verificationUrl || ""}
                      disabled={disabled}
                      onChange={(event) =>
                        changeLicense(index, {
                          verificationUrl: event.target.value,
                        })
                      }
                    />
                  </Grid>
                </Grid>
                {index < draft.regulatoryLicenses.length - 1 && (
                  <Divider sx={{ mt: 2 }} />
                )}
              </Box>
            ))}
            <Box>
              <Button
                startIcon={<AddIcon />}
                disabled={disabled}
                onClick={() =>
                  change({
                    regulatoryLicenses: [
                      ...draft.regulatoryLicenses,
                      emptyLicense(),
                    ],
                  })
                }
              >
                添加监管牌照
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            多语言声明
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            必须包含 en_US。内容只允许纯文本，前台不会解析 HTML。
          </Typography>
          <Stack spacing={2}>
            {draft.statements.map((statement, index) => (
              <Stack direction="row" spacing={1} key={index} alignItems="start">
                <TextField
                  select
                  label="语言"
                  value={statement.locale}
                  disabled={disabled}
                  placeholder="en_US"
                  sx={{ width: 150 }}
                  onChange={(event) =>
                    changeStatement(index, { locale: event.target.value })
                  }
                >
                  {Locales.map((locale) => (
                    <MenuItem value={locale} key={locale}>
                      {toLocaleName(locale as LocalesType)}（{locale}）
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label={`声明内容 ${index + 1}`}
                  value={statement.value}
                  disabled={disabled}
                  onChange={(event) =>
                    changeStatement(index, { value: event.target.value })
                  }
                />
                <IconButton
                  aria-label={`删除声明 ${index + 1}`}
                  disabled={disabled || draft.statements.length === 1}
                  onClick={() =>
                    change({
                      statements: draft.statements.filter(
                        (_, statementIndex) => statementIndex !== index
                      ),
                    })
                  }
                >
                  <DeleteOutlineIcon />
                </IconButton>
              </Stack>
            ))}
            <Box>
              <Button
                startIcon={<AddIcon />}
                disabled={disabled}
                onClick={() =>
                  change({
                    statements: [
                      ...draft.statements,
                      { locale: "", value: "" },
                    ],
                  })
                }
              >
                添加语言
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom>
            法律文档绑定
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="用户协议文档 ID"
                value={draft.documentBindings.termsDocumentId ?? ""}
                disabled={disabled}
                onChange={(event) =>
                  changeBindings({
                    termsDocumentId: parseDocumentId(event.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="隐私政策文档 ID"
                value={draft.documentBindings.privacyDocumentId ?? ""}
                disabled={disabled}
                onChange={(event) =>
                  changeBindings({
                    privacyDocumentId: parseDocumentId(event.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="风险披露文档 ID（可选）"
                value={draft.documentBindings.riskWarningDocumentId ?? ""}
                disabled={disabled}
                onChange={(event) =>
                  changeBindings({
                    riskWarningDocumentId: parseDocumentId(event.target.value),
                  })
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="AML 文档 ID（可选）"
                value={draft.documentBindings.amlDocumentId ?? ""}
                disabled={disabled}
                onChange={(event) =>
                  changeBindings({
                    amlDocumentId: parseDocumentId(event.target.value),
                  })
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Stack>
  );
};
